import { Component, HostListener } from '@angular/core';
import { ApiService } from 'app/api.service';
import { AppService } from 'app/app.service';
import { UIModalBaseComponent } from 'app/ui/ui-modal-base.component';

/**
 * "Have you completed…?" — the work a stage asked for before a quote step (CRM-143).
 *
 * Global, like the dialog and the notifications, because unrelated screens raise it: the three
 * quote actions that consult the checkpoint, and the deal's stage moves and Mark as Won. Putting
 * the component in each of their templates would be that many places to keep in step, and one of
 * them lives inside another modal.
 *
 * Two ways in, because the callers are not the same shape:
 *
 *  - hold(error, retry) — an operation was refused. The payload the server sent carries the list,
 *    so nothing is fetched, and Continue re-runs the exact operation the user started. Never a
 *    generic fallback: resuming with something else is how a user ends up somewhere they did not
 *    ask to be.
 *  - check(crmId, event, proceed) — for a step the CRM checks on its own side: a stage move
 *    (heldMove()) or winning the deal. Asks first, then proceeds.
 *
 * It is a checkpoint, not a lock, and the wording says so: the items are what the deal was *asked*
 * for. Somebody who needs to move past one can still set the quote's status directly, and that is
 * recorded rather than hidden — so this screen does not pretend to be a wall.
 */
@Component({
	selector: 'app-core-required-work-modal',
	templateUrl: './core-required-work-modal.component.html'
})
export class CoreRequiredWorkModalComponent extends UIModalBaseComponent {

	// The step being held, in the words the deal screen uses. Keyed by the backend's own event
	// ids, which are dotted rather than camelCase — they are data, not identifiers.
	/* eslint-disable @typescript-eslint/naming-convention */
	private static readonly STEPS = {
		'quote.review.submitted': 'submitting the quote for review',
		'quote.review.passed': 'passing review',
		'quote.sent': 'sending the quote'
	};
	/* eslint-enable @typescript-eslint/naming-convention */

	crmId = null;
	event = '';
	// Set when the step is not one of STEPS — a stage move names the lane it was going to.
	stepLabel = '';
	items = [];
	disabled = false;
	loading = false;
	// The question whose answer is in flight. Its branch runs inside the request and can take seconds.
	answeringId: number = null;
	// A site visit is being booked in another tab; the list is re-read when this one is back.
	private awaitingBooking = false;

	// What Continue does. Held rather than re-derived so the original operation is what resumes.
	private resume = null;

	// What Cancel does, when the caller has something to undo or redraw. Optional, because most
	// callers have nothing: a refused operation simply did not happen. The ones that do are the
	// ones this dialog stands in front of — the deal's own screens, where somebody may have
	// ticked two items complete and then backed out, leaving Focus showing work that is done.
	private abandon = null;

	constructor(
		public app: AppService,
		private api: ApiService
	) {
		super();
	}

	get stepName() {
		return this.stepLabel || CoreRequiredWorkModalComponent.STEPS[this.event] || 'this step';
	}

	// A quote step can be forced through by setting the quote's status directly, and the dialog
	// says so. A stage move cannot: there is no second route, the deal simply stays where it is,
	// so promising an escape hatch that does not exist would be worse than saying nothing.
	get hasEscapeHatch() {
		return !this.stepLabel;
	}

	get outstanding() {
		return (this.items || []).length;
	}

	// Whether follow-ups count for what is being checked. Set by check(); see the note there.
	private holdsFollowUps = false;

	// Set by heldMove(): an answer can move the deal itself, and then the move must not resume.
	private holdsMove = false;

	/**
	 * An operation came back refused. Returns true when it handled the error, so the caller can
	 * fall through to its usual message for everything else.
	 */
	hold(error, retry) {
		const payload = error?.payload;
		if (error?.code !== 40 || !payload?.requirements?.length) return false;

		this.resume = retry;
		this.holdsMove = false;
		this.open({ crm_id: payload.crm_id, event: payload.event, items: payload.requirements });
		return true;
	}

	/**
	 * A stage move was refused (api-crm's ERROR_REQUIRED_WORK).
	 *
	 * Fetched rather than read out of the payload, unlike hold(): that refusal names at most five
	 * items and carries no ids, and an id is what Mark complete needs. Fetching also means the
	 * list is current if somebody cleared an item while the card was being dragged — and if it
	 * comes back empty the move just happens, with no dialog to dismiss.
	 */
	heldMove(crmId, stageName, retry, cancelled = null) {
		this.check(crmId, null, retry, stageName ? 'moving this deal to ' + stageName : 'moving this deal', cancelled);
		this.holdsMove = true;
	}

	check(crmId, event, proceed, stepLabel = '', cancelled = null) {
		/**
		 * Only a stage move is held by follow-ups.
		 *
		 * The quote-side checkpoint is its own gate (api-quote's `checkpoint()`, code 40) and asks
		 * about required work alone, on three named operations. Listing follow-ups for one of those
		 * would be this dialog inventing a wall the server does not have — refusing somebody in
		 * the name of a rule nothing enforces is worse than not mentioning it.
		 *
		 * A stage move arrives with a step label and no event; a quote step arrives with an event.
		 */
		this.holdsFollowUps = !event;
		this.holdsMove = false;
		this.abandon = cancelled;
		this.loading = true;
		this.api.crm.requirements(crmId, data => {
			this.loading = false;
			// Everything the server will refuse on, in the order it refuses in: required work
			// first, then open follow-ups. `skipped` is also on the response and is neither —
			// nobody can act on it and it holds nothing, so it is not read here.
			//
			// Reading the whole object as the list is how this once silently stopped holding
			// anything: an object has no length, so every move looked unheld.
			const items = this.holdsFrom(data);
			if (!items.length) { proceed(); return; }

			this.resume = proceed;
			this.open({ crm_id: crmId, event, items, step_label: stepLabel });
		}, () => {
			// The checkpoint is an assistant, not a gate. If it cannot be read, the work carries
			// on — and the server still refuses anything that genuinely must not happen.
			this.loading = false;
			proceed();
		});
	}

	open(data: any) {
		this.crmId = data.crm_id;
		this.event = data.event;
		this.stepLabel = data.step_label || '';
		this.items = data.items || [];
		this.disabled = false;
		this.answeringId = null;
		this.awaitingBooking = false;
		this.modal.open();
	}

	@HostListener('window:focus')
	onWindowFocus() {
		// Every return, not just the first: somebody may look back before they have booked.
		if (!this.awaitingBooking || !this.modal.isOpen) return;
		this.app.onAttentionChange.emit(Number(this.crmId));
		this.reload();
	}

	/**
	 * The two kinds of thing that hold a deal, as one list.
	 *
	 * Required work is resolved and a follow-up is completed — two verbs, two endpoints, one
	 * question for the person reading: is this done. `kind` is what tells the row which button it
	 * has and which call it makes.
	 */
	private holdsFrom(data) {
		const required = (data?.list || []).map(r => ({ ...r, kind: 'required' }));
		if (!this.holdsFollowUps) return required;

		const followUps = (data?.follow_ups || []).map(f => ({ ...f, kind: 'follow_up' }));
		return required.concat(followUps);
	}

	// Re-read: one action can clear or raise other rows. `gone` is dropped if the re-read fails.
	private reload(gone = null) {
		this.disabled = true;
		this.api.crm.requirements(this.crmId, data => {
			this.disabled = false;
			this.items = this.holdsFrom(data);
		}, () => {
			this.disabled = false;
			if (gone) this.items = this.items.filter(i => !(i.kind === gone.kind && i.id === gone.id));
		});
	}

	markComplete(item) {
		this.disabled = true;

		const done = () => {
			// The widget lists the same two things this modal does, from every deal at once, and the
			// deal's own page is under this dialog showing them too — so the deal is named.
			this.app.onAttentionChange.emit(Number(this.crmId));
			this.reload(item);
		};

		const fail = error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		};

		if (item.kind === 'follow_up') this.api.crm.completeFollowUp(item.id, done, fail);
		else this.api.crm.resolveRequirement({ id: item.id }, done, fail);
	}

	// Answered Yes or No rather than ticked: the site-visit question, or one of the organisation's own.
	isQuestion(item) {
		return item.resolve_mode === 'answer' || item.resolve_mode === 'question';
	}

	// The answer picks the automation's branch.
	answer(item, answer: boolean) {
		if (this.answeringId !== null) return;
		this.disabled = true;
		this.answeringId = item.id;

		const after = () => {
			this.answeringId = null;
			this.app.onAttentionChange.emit(Number(this.crmId));
			this.reload();
		};

		const data = { crm_id: this.crmId, id: item.id, answer: answer ? 1 : 0 };
		const send = item.resolve_mode === 'question' ? this.api.crm.answerQuestion : this.api.crm.answerSiteVisit;
		send(data, result => {
			// The answer's branch already put the deal somewhere, so repeating the drag would undo it.
			if (this.holdsMove && result?.moved_to) {
				this.answeringId = null;
				this.app.onAttentionChange.emit(Number(this.crmId));
				this.app.notifications.showInfo('The automation moved this deal to ' + (result.moved_to.name || 'another stage') + '.');
				if (this.modal.isOpen) this.cancelButton();
				return;
			}
			after();
		}, error => {
			this.app.notifications.showDanger(error.message);
			// It may have saved before failing, so re-read either way
			after();
		});
	}

	// Booked, not ticked: the calendar with Workhub, otherwise the deal's own Meeting tab.
	bookSiteVisit() {
		this.awaitingBooking = true;
		if (this.app.orgInfo?.allow_work_hub) {
			window.open('/' + this.app.org + '/schedule/calendar?crm=' + this.crmId + '&site_visit=1', '_blank');
		} else {
			window.open('/' + this.app.org + '/crm/deals/' + this.crmId + '?book=site_visit', '_blank');
		}
	}

	openDeal() {
		this.modal.close();
		window.open('/' + this.app.org + '/crm/deals/' + this.crmId, '_blank');
	}

	continueButton() {
		const go = this.resume;
		this.resume = null;
		this.abandon = null;
		this.modal.close();
		if (go) go();
	}

	cancelButton() {
		const back = this.abandon;
		this.resume = null;
		this.abandon = null;
		this.modal.close();
		if (back) back();
	}

}
